import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { FormFrame, PcfStep1, PcfStep2, PcfStep3 } from "@/components/landing/product-mocks";
import { useIsEs } from "@/components/language-provider";

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Design size of the "screen". The film's last frame is this window rendered at 1920×1080 (same 16:9), and both are
// contain-fitted on the same #F3F2F1 background, so the zoom into the laptop hands off to this section without a seam.
export const DYN_W = 1120;
export const DYN_H = 630;
const TRAVEL = 240; // vh of scroll spent inside Dynamics (the form body scrolls through the 3 PCF steps)

const COPY = {
  pt: [
    ["Documento e assinantes", "Escolha upload ou template e quem assina: contatos e usuários do registro, ou externos."],
    ["IA posiciona a assinatura", "A IA sugere onde cada assinatura entra. Confirme ou arraste."],
    ["Status, reenvio e auditoria", "Acompanhe cada assinante, reenvie por e-mail ou WhatsApp e baixe com auditoria."],
  ],
  en: [
    ["Document and signers", "Pick upload or template and who signs: record contacts and users, or external."],
    ["AI places the signature", "AI suggests where each signature goes. Confirm or drag."],
    ["Status, resend and audit trail", "Track each signer, resend by email or WhatsApp and download with the audit trail."],
  ],
  es: [
    ["Documento y firmantes", "Elija carga o plantilla y quién firma: contactos y usuarios del registro, o externos."],
    ["La IA posiciona la firma", "La IA sugiere dónde va cada firma. Confirme o arrastre."],
    ["Estado, reenvío y auditoría", "Siga a cada firmante, reenvíe por correo o WhatsApp y descargue con la auditoría."],
  ],
} as const;

const wideQuery = "(min-width: 1024px) and (orientation: landscape)";

export function DynamicsImmersive({ pt }: { pt: boolean }) {
  const es = useIsEs();
  const ref = useRef<HTMLElement>(null);
  const [shift, setShift] = useState(0);
  const [step, setStep] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const s2Ref = useRef<HTMLDivElement>(null);
  const s3Ref = useRef<HTMLDivElement>(null);
  const tops = useRef<number[]>([0, 1e9, 1e9]); // window-relative top of each step at body offset 0
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(wideQuery).matches);
  const [scale, setScale] = useState(1);
  const copy = COPY[pt ? "pt" : es ? "es" : "en"];

  useEffect(() => {
    const mq = window.matchMedia(wideQuery);
    const fit = () => { setWide(mq.matches); setScale(Math.min(window.innerWidth / DYN_W, (window.innerHeight - 64) / DYN_H)); };
    fit();
    window.addEventListener("resize", fit);
    mq.addEventListener("change", fit);
    return () => { window.removeEventListener("resize", fit); mq.removeEventListener("change", fit); };
  }, []);

  // How far the form body must scroll to bring the third step into view (design px, unaffected by the transform).
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!wide || !body) return;
    const measure = () => {
      setShift(Math.max(0, body.offsetTop + body.offsetHeight - DYN_H + 12));
      const b = body.getBoundingClientRect().top - body.offsetTop;
      tops.current = [0, s2Ref.current, s3Ref.current].map((el) => (el ? (el as HTMLElement).getBoundingClientRect().top - b : 0));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => ro.disconnect();
  }, [wide, pt]);

  useGSAP(() => {
    if (!wide || !ref.current) return;
    const el = ref.current;
    const q = gsap.utils.selector(el);
    // Scrolling the page scrolls the Dynamics form itself (command bar and record header stay put, like the real app);
    // the explanation cards slide along in step. The window is invisible while it overlaps the film and switches on
    // exactly when the film's last frame (this same window) is on screen, so nothing visibly changes.
    const tl = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: el, start: "top top", end: "bottom bottom", scrub: 0.6,
        onToggle: (self) => { if (self.isActive) el.dataset.live = "1"; },
        onLeaveBack: () => { el.dataset.live = "0"; },
        onUpdate: (self) => {
          // the card follows whichever step has scrolled into the upper part of the form
          const y = -self.progress * shift;
          let i = 0;
          for (let k = 1; k < 3; k++) if (tops.current[k] + y <= 290) i = k;
          setStep((cur) => { if (cur !== i) setPrev(cur); return i; });
        },
      },
    });
    tl.to(bodyRef.current, { y: -shift, duration: 1 }, 0);
    void q;
  }, { scope: ref, dependencies: [wide, shift], revertOnUpdate: true });

  useGSAP(() => {
    if (!wide) return;
    const q = gsap.utils.selector(ref.current);
    gsap.fromTo(q("[data-card='in']"), { x: 64, opacity: 0 }, { x: 0, opacity: 1, duration: 0.55, ease: "power3.out", overwrite: true });
    if (prev !== null) gsap.to(q("[data-card='out']"), { x: -48, opacity: 0, duration: 0.28, ease: "power2.in", overwrite: true, onComplete: () => setPrev(null) });
  }, { scope: ref, dependencies: [step] });

  const callout = (i: number) => (
    <div className="rounded-lg border border-black/5 bg-white p-5 shadow-[0_18px_40px_-24px_rgba(27,42,30,0.45)]">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--fs-moss)]">{i + 1} / 3</div>
        <div className="flex gap-1.5">{[0, 1, 2].map((k) => <span key={k} className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${k === i ? "bg-[var(--fs-sage)]" : "bg-black/10"}`} />)}</div>
      </div>
      <div className="mt-2 text-lg font-semibold leading-snug tracking-[-0.01em] text-[var(--fs-forest)]">{copy[i][0]}</div>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{copy[i][1]}</p>
    </div>
  );

  if (!wide) {
    // Phones and small tablets: the same PCF steps as a normal, readable section.
    return (
      <section id="dynamics" data-dyn className="scroll-mt-20 bg-[#F3F2F1] py-12">
        <div className="mx-auto max-w-6xl space-y-10 px-5 sm:px-8">
          {[PcfStep1, PcfStep2, PcfStep3].map((S, i) => (
            <div key={i} className="space-y-4">
              {callout(i)}
              <div className="overflow-hidden rounded-xl border border-black/10 shadow-sm"><FormFrame pt={pt}><S pt={pt} /></FormFrame></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="dynamics" ref={ref} data-dyn data-live="0" className="fs-dyn-track relative z-10"
      style={{ ["--fs-dyn-travel" as string]: TRAVEL } as CSSProperties}>
      <div className="fs-dyn-stage sticky top-0 flex items-center justify-center overflow-hidden bg-[#F3F2F1] pt-16">
        <div className="shrink-0" style={{ width: DYN_W, height: DYN_H, transform: `scale(${scale})` }}>
          <div className="relative h-full overflow-hidden bg-[#F3F2F1]">
            <FormFrame pt={pt} bodyRef={bodyRef}>
              <PcfStep1 pt={pt} />
              <div ref={s2Ref} className="mt-5 border-t border-black/5 pt-5"><PcfStep2 pt={pt} bare /></div>
              <div ref={s3Ref} className="mt-5 border-t border-black/5 pt-5"><PcfStep3 pt={pt} bare /></div>
            </FormFrame>
            <div className="absolute left-3 top-[150px] w-[440px]">
              {prev !== null && prev !== step && <div data-card="out" className="absolute inset-x-0 top-0">{callout(prev)}</div>}
              <div key={step} data-card="in">{callout(step)}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
